import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { Button, Stack } from '@mui/material';
import { Nullable, SelectOption } from 'global';
import { capitalizeFirstLetter } from 'formatters';
import {
  FilterCardContainer,
  FilterEvent,
  ListOfPresets,
  PresetFilter,
  PresetId,
  PresetMode,
  PresetsForm,
} from 'components/shared/Filters';
import { FilterTableType, TableFilter } from '@trnsact/trnsact-shared-types';
import { RenderWithCondition } from 'components/shared/Conditional/RenderWithCondition';
import { VOFilters, VOFilterType, FiltersContainer, initialVOFiltersValue } from 'modules/vendorOpportunity/filters';

import { usePresets } from '../../../api';

interface PresetModeState {
  type: PresetMode;
  presetId: Nullable<PresetId>;
  filters: Nullable<Partial<VOFilters>>;
}

interface Props {
  isOpen: boolean;
  onClose: VoidFunction;
  listOfPresets: TableFilter[];
  setOfFilters: VOFilterType[];
  defaultExternalFiltersValue?: Nullable<Partial<VOFilters>>;
  externalMeta?: Partial<Record<VOFilterType, SelectOption[]>>;
  onChanged: (filters: Partial<VOFilters>, event: FilterEvent) => void;
}

export const TableFilters = ({
  listOfPresets = [],
  isOpen,
  onClose,
  onChanged,
  setOfFilters,
  externalMeta,
  defaultExternalFiltersValue = null,
}: Props) => {
  const { handleUpdateFilter, handleCreateFilter, handleDeleteFilter, isFilterUpdating, isFilterDeleting } =
    usePresets();

  const [externalFiltersValue, setExternalFiltersValue] =
    useState<Nullable<Partial<VOFilters>>>(defaultExternalFiltersValue);
  const [mode, setMode] = useState<PresetModeState>({ filters: null, presetId: null, type: PresetMode.OFF });

  const presetsMap = listOfPresets.reduce<Record<PresetId, PresetFilter<VOFilters>>>(
    (acc, { name, tableFilterId, isDefault, config }) => {
      acc[tableFilterId] = {
        name,
        id: tableFilterId,
        isSelected: isDefault,
        value: !Object.keys(config).length ? initialVOFiltersValue : config,
      };

      return acc;
    },
    {}
  );

  const presetFormVisibleState: PresetMode[] = [PresetMode.CREATE, PresetMode.UPDATE];
  const isPresetFormVisible = presetFormVisibleState.includes(mode.type);

  useEffect(() => {
    setExternalFiltersValue(defaultExternalFiltersValue);
  }, [defaultExternalFiltersValue]);

  const handleDeletePreset = async (presetId: PresetId) => {
    await handleDeleteFilter(presetId);
  };

  const handleSelectPreset = (presetId: PresetId) => {
    const nextFilters = presetsMap[presetId].value;

    const mappedDates = {
      [VOFilterType.CREATE_DATE_RANGE]: {
        from: moment(presetsMap[presetId].value[VOFilterType.CREATE_DATE_RANGE].from),
        to: moment(presetsMap[presetId].value[VOFilterType.CREATE_DATE_RANGE].to),
      },
    };

    const filterValue = { ...nextFilters, ...mappedDates };

    setExternalFiltersValue(filterValue);
  };

  const handleMoveToEditPresetMode = (presetId: PresetId, filters: Partial<VOFilters>) => {
    setMode({ type: PresetMode.UPDATE, filters, presetId: presetId });
  };

  const handleMoveToCreatePresetMode = (filters: Partial<VOFilters>) => {
    setMode({ type: PresetMode.CREATE, filters, presetId: null });
  };

  const handleMoveToOffPresetMode = () => {
    setMode({ type: PresetMode.OFF, filters: null, presetId: null });
  };

  const handleSavePreset = async (name: string, filters: Partial<VOFilters>) => {
    if (mode.type === PresetMode.UPDATE && mode.presetId) {
      await handleUpdateFilter({ name, config: filters, tableFilterId: mode.presetId });
    } else {
      await handleCreateFilter({ name, config: filters, isDefault: false, tableType: FilterTableType.Opportunities });
    }

    handleMoveToOffPresetMode();
  };

  return (
    <FiltersContainer
      open={isOpen}
      onClose={onClose}
      onChanged={onChanged}
      externalMeta={externalMeta}
      configuration={setOfFilters}
      externalFiltersValue={externalFiltersValue}
      headerNode={filters => (
        <Button
          size="small"
          variant="text"
          onClick={() => handleMoveToCreatePresetMode(filters)}
          disabled={presetFormVisibleState.includes(mode.type)}
        >
          Save Filter View
        </Button>
      )}
      presetsNode={filters => (
        <Stack gap="0.5rem">
          <RenderWithCondition condition={isPresetFormVisible}>
            <FilterCardContainer label={`${capitalizeFirstLetter(mode.type)} Filter`}>
              <PresetsForm<Partial<VOFilters>>
                key={mode.type}
                onCancel={handleMoveToOffPresetMode}
                preset={mode.presetId ? presetsMap[mode.presetId] : null}
                onSave={({ name }) => handleSavePreset(name, filters)}
              />
            </FilterCardContainer>
          </RenderWithCondition>

          <FilterCardContainer label="Saved Filters">
            <ListOfPresets<Partial<VOFilters>>
              list={Object.values(presetsMap)}
              actionsLoading={isFilterUpdating || isFilterDeleting}
              actions={{
                onDelete: handleDeletePreset,
                onSelect: handleSelectPreset,
                onCreate: () => handleMoveToCreatePresetMode(filters),
                onEdit: presetId => handleMoveToEditPresetMode(presetId, filters),
              }}
            />
          </FilterCardContainer>
        </Stack>
      )}
    />
  );
};
